import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function AdminRecognitions() {
  const navigate = useNavigate();
  const [recognitions, setRecognitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRecognition, setEditingRecognition] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    titleUrdu: '',
    description: '',
    descriptionUrdu: '',
    image: '',
    year: '',
    category: '',
    categoryUrdu: '',
    order: 0,
    isActive: true
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchRecognitions();
  }, []);

  const fetchRecognitions = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin/login");
        return;
      }

      const response = await api.get("/admin/recognitions/all", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecognitions(response.data.recognitions || []);
    } catch (error) {
      console.error("Fetch recognitions error:", error);
      toast.error("Recognitions لوڈ کرنے میں ناکام");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      titleUrdu: '',
      description: '',
      descriptionUrdu: '',
      image: '',
      year: '',
      category: '',
      categoryUrdu: '',
      order: 0,
      isActive: true
    });
    setEditingRecognition(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("adminToken");
      const payload = {
        title: formData.title,
        titleUrdu: formData.titleUrdu,
        description: formData.description,
        descriptionUrdu: formData.descriptionUrdu,
        image: formData.image,
        year: formData.year,
        category: formData.category,
        categoryUrdu: formData.categoryUrdu,
        order: formData.order,
        isActive: formData.isActive
      };

      if (editingRecognition) {
        await api.put(`/admin/recognitions/${editingRecognition._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Recognition اپڈیٹ ہو گیا");
      } else {
        await api.post("/admin/recognitions", payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Recognition شامل ہو گیا");
      }

      setShowAddModal(false);
      resetForm();
      fetchRecognitions();
    } catch (error) {
      console.error("Save recognition error:", error);
      toast.error(error.response?.data?.message || "Recognition محفوظ کرنے میں ناکام");
    }
  };

  const handleEdit = (recognition) => {
    setEditingRecognition(recognition);
    setFormData({
      title: recognition.title || '',
      titleUrdu: recognition.titleUrdu || '',
      description: recognition.description || '',
      descriptionUrdu: recognition.descriptionUrdu || '',
      image: recognition.image || '',
      year: recognition.year || '',
      category: recognition.category || '',
      categoryUrdu: recognition.categoryUrdu || '',
      order: recognition.order || 0,
      isActive: recognition.isActive !== undefined ? recognition.isActive : true
    });
    setShowAddModal(true);
  };

  const handleDelete = async (recognitionId) => {
    if (!window.confirm('کیا آپ واقعی یہ recognition حذف کرنا چاہتے ہیں؟')) {
      return;
    }

    try {
      const token = localStorage.getItem("adminToken");
      await api.delete(`/admin/recognitions/${recognitionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Recognition حذف ہو گیا");
      fetchRecognitions();
    } catch (error) {
      console.error("Delete recognition error:", error);
      toast.error("Recognition حذف کرنے میں ناکام");
    }
  };

  return (
    <>
      <Helmet><title>Recognitions Management - Admin Panel</title></Helmet>
      
      <div className="min-h-screen bg-brand-background">
        {/* Mobile Menu Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Sidebar - Fixed */}
          <aside className={`fixed top-0 left-0 h-screen w-64 bg-white shadow-md border-r border-brand-card/60 z-40 overflow-y-auto transform transition-transform duration-300 ease-in-out ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}>
            <div className="p-4 border-b border-brand-card/40 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-brand-header">Admin Navigation</h2>
                <p className="text-xs text-gray-500 mt-1">Quick access to tools</p>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav className="p-3 space-y-2 text-sm">
              <button
                onClick={() => {
                  navigate('/admin');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Dashboard</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/bills');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Bill Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Field Settings</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/roznamacha-fields');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Roz Namacha Fields</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recoveries');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recovery Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/expenses');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Expense Management</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/payment-methods');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Payment Methods</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/team-members');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Team Members</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/recognitions');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Recognitions</span>
              </button>
              <button
                onClick={() => {
                  navigate('/admin/blogs');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Blogs</span>
              </button>
              <button
                onClick={() => {
                  navigate('/company/dashboard');
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Company Dashboard</span>
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem("adminToken");
                  localStorage.removeItem("adminData");
                  navigate("/admin/login");
                  setSidebarOpen(false);
                }}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-gray-800 hover:bg-brand-background hover:text-brand-header transition"
              >
                <span>Logout</span>
              </button>
            </nav>
          </aside>

          {/* Main Content */}
          <div className="flex-1 md:ml-64 p-4 md:p-8">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-brand-card/60">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-brand-header">Recognitions Management</h1>
                  <p className="text-gray-600 mt-1 text-sm md:text-base">Manage recognitions and awards for the website</p>
                </div>
                <button
                  onClick={() => {
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 md:px-6 py-2 md:py-3 rounded-lg transition transform hover:-translate-y-0.5 hover:shadow-lg"
                >
                  + Add Recognition
                </button>
              </div>
            </div>

            {/* Recognitions List */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden border border-brand-card/60">
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading recognitions...</p>
                </div>
              ) : recognitions.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-gray-600">No recognitions found. Add your first recognition!</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-brand-background">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Image
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Year
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recognitions.map((recognition) => (
                        <tr key={recognition._id} className="hover:bg-brand-background/60 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {recognition.image ? (
                              <img src={recognition.image} alt={recognition.title} className="h-16 w-24 object-cover rounded" />
                            ) : (
                              <div className="h-16 w-24 bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs">
                                No Image
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{recognition.title}</div>
                            {recognition.titleUrdu && (
                              <div className="text-sm text-gray-500">{recognition.titleUrdu}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{recognition.year}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{recognition.category || '-'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{recognition.order}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              recognition.isActive
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-red-50 text-brand-alert border border-red-200"
                            }`}>
                              {recognition.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleEdit(recognition)}
                              className="text-indigo-600 hover:text-indigo-900 mr-4"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(recognition._id)}
                              className="text-brand-alert hover:text-red-900"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 my-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingRecognition ? 'Edit Recognition' : 'Add New Recognition'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title (English) *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Best Business Solution 2024"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title (Urdu)
                    </label>
                    <input
                      type="text"
                      name="titleUrdu"
                      value={formData.titleUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="بہترین کاروباری حل 2024"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description (English)
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Awarded for excellence..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description (Urdu)
                    </label>
                    <textarea
                      name="descriptionUrdu"
                      value={formData.descriptionUrdu}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="بہترین کارکردگی کے لیے ایوارڈ..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      type="text"
                      name="year"
                      value={formData.year}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="2024"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Order
                    </label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category (English)
                    </label>
                    <input
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="Business Excellence"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category (Urdu)
                    </label>
                    <input
                      type="text"
                      name="categoryUrdu"
                      value={formData.categoryUrdu}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600"
                      placeholder="کاروباری بہترین"
                    />
                  </div>
                </div>

                <div className="flex items-center pt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition"
                  >
                    {editingRecognition ? 'Update' : 'Add'} Recognition
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default AdminRecognitions;

